// AddressAutocomplete.jsx
// Staff-themed wrapper around the Places API (New) REST autocomplete.
// Modeled on the booking inquiry form's CityAutocomplete (QuotePage.jsx) but
// dark-themed + proximity-biased toward St. Louis by default since that's
// where 95% of shifts happen.
//
// Falls back to free-form text if the Places API is unreachable (offline,
// missing key, rate-limited) — Dan can always type a venue name by hand.
//
// Props:
//   value         (string)   current input value
//   onChange      (fn)       called with the new string on every keystroke + select
//   placeholder   (string)   input placeholder
//   inputStyle    (object)   caller-supplied styling for the input element
//   biasCenter    ({lat,lng,radiusMeters?}) optional proximity-bias override
//   required      (bool)     pass-through to the input

import { useState, useRef, useEffect } from "react";

const PLACES_API_BASE = "https://places.googleapis.com/v1";

// St. Louis metro default — 50km radius covers Tower Grove, St. Charles,
// Belleville IL, etc. Override via biasCenter for events outside the region.
const STL_BIAS = { lat: 38.6270, lng: -90.1994, radiusMeters: 50000 };

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  inputStyle,
  biasCenter = STL_BIAS,
  required = false,
}) {
  const [focused, setFocused]         = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [hoverIdx, setHoverIdx]       = useState(-1);
  const sessionTokenRef = useRef(null);
  const debounceRef     = useRef(null);
  const inputRef        = useRef(null);

  useEffect(() => {
    sessionTokenRef.current =
      (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
  }, []);

  const fetchSuggestions = (input) => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
    if (!key || !input || input.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const body = {
          input,
          includedRegionCodes: ["us"],
          sessionToken: sessionTokenRef.current,
        };
        if (biasCenter?.lat != null && biasCenter?.lng != null) {
          body.locationBias = {
            circle: {
              center:      { latitude: biasCenter.lat, longitude: biasCenter.lng },
              radius:      biasCenter.radiusMeters || 50000,
            },
          };
        }
        const resp = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
          method:  "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": key,
          },
          body: JSON.stringify(body),
        });
        if (!resp.ok) { setSuggestions([]); return; }
        const data = await resp.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.slice(0, 6) : []);
        setHoverIdx(-1);
      } catch (err) {
        console.warn("AddressAutocomplete error:", err);
        setSuggestions([]);
      }
    }, 220);
  };

  const selectSuggestion = async (suggestion) => {
    const pred = suggestion?.placePrediction;
    if (!pred) return;
    const predictionText = pred.text?.text || "";
    onChange(predictionText);
    setSuggestions([]);
    setHoverIdx(-1);

    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
    if (!pred.placeId || !key) return;
    try {
      const resp = await fetch(
        `${PLACES_API_BASE}/places/${pred.placeId}?sessionToken=${encodeURIComponent(sessionTokenRef.current)}`,
        {
          headers: {
            "X-Goog-Api-Key":   key,
            "X-Goog-FieldMask": "displayName,formattedAddress,addressComponents",
          },
        }
      );
      if (!resp.ok) return;
      const place = await resp.json();
      const displayName = place.displayName?.text;
      const formatted   = (place.formattedAddress || "").replace(/,\s*USA$/i, "").trim();

      // Venue field policy: always keep the most specific address we can.
      // If the formatted address already starts with the POI name (e.g. the
      // place is just a city), use formatted as-is. Otherwise prepend the
      // displayName so "Tower Grove Farmers Market, 4256 Magnolia Ave, St.
      // Louis, MO 63110" survives. Never collapse to "City, State" — the
      // booking-inquiry form does that, but a staff shift needs the exact
      // location for directions.
      let composed;
      if (displayName && formatted && !formatted.toLowerCase().startsWith(displayName.toLowerCase())) {
        composed = `${displayName}, ${formatted}`;
      } else {
        composed = formatted || displayName || "";
      }
      if (composed) onChange(composed);
      sessionTokenRef.current =
        (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
    } catch (err) {
      console.warn("Place details fetch failed:", err);
    }
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setHoverIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && hoverIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[hoverIdx]); }
    else if (e.key === "Escape") { setSuggestions([]); setHoverIdx(-1); }
  };

  const showDropdown = focused && suggestions.length > 0;

  const mergedInputStyle = {
    ...inputStyle,
    borderBottomLeftRadius:  showDropdown ? 0 : (inputStyle?.borderRadius ?? 10),
    borderBottomRightRadius: showDropdown ? 0 : (inputStyle?.borderRadius ?? 10),
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={handleKeyDown}
        style={mergedInputStyle}
      />
      {showDropdown && (
        <ul style={{
          listStyle: "none",
          margin: 0, padding: 0,
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
          background: "#1a2342",
          border: "1px solid rgba(255,218,173,0.2)",
          borderTop: "1px solid rgba(255,218,173,0.12)",
          borderBottomLeftRadius: 10, borderBottomRightRadius: 10,
          boxShadow: "0 14px 30px rgba(0,0,0,0.45)",
          maxHeight: 280, overflowY: "auto",
        }}>
          {suggestions.map((s, i) => {
            const pred = s.placePrediction;
            const main = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
            const sec  = pred?.structuredFormat?.secondaryText?.text || "";
            return (
              <li
                key={pred?.placeId || i}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                onMouseEnter={() => setHoverIdx(i)}
                style={{
                  padding: "0.65rem 0.85rem",
                  cursor:  "pointer",
                  background: hoverIdx === i ? "rgba(252,179,31,0.1)" : "transparent",
                  borderBottom: i < suggestions.length - 1 ? "1px solid rgba(255,218,173,0.08)" : "none",
                  fontSize: "0.88rem",
                }}
              >
                <div style={{ color: "#ffdaad", fontWeight: 600 }}>{main}</div>
                {sec && <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.76rem", marginTop: 2 }}>{sec}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
